// =============================================================================
// GraphQL Queries — ResolveGuru AppSync
// Only incident-related queries are active for Phase 1 (ServiceNowResolver page)
// =============================================================================

export const getIncident = /* GraphQL */ `
  query GetIncident($incident_number: String!, $datetime: String!) {
    getIncident(incident_number: $incident_number, datetime: $datetime) {
      incident_number
      datetime
      short_description
      description
      predicted_class
      is_confident
      confidence
      overall_correctness
      overall_text_feedback
      step_feedback
      modified_by
      modified_at
    }
  }
`;

export const listIncidents = /* GraphQL */ `
  query ListIncidents($limit: Int, $nextToken: String) {
    listIncidents(limit: $limit, nextToken: $nextToken) {
      items {
        incident_number
        datetime
        short_description
        description
        predicted_class
        is_confident
        confidence
        overall_correctness
        overall_text_feedback
        step_feedback
        modified_by
        modified_at
      }
      nextToken
    }
  }
`;

// =============================================================================
// Prod Performance Queries
// =============================================================================

export const getProdPerformance = /* GraphQL */ `
  query GetProdPerformance($product: String!, $timeline: String!) {
    getProdPerformance(product: $product, timeline: $timeline) {
      product
      timeline
      bedrock_analysis
      cached_at
      source
    }
  }
`;

// =============================================================================
// Tribe Performance / Tribe Summary Queries — intentionally absent
//
// The AppSync schema exposes NO `getTribePerformance` query and its
// `getTribeSummary` query is wired to a resolver whose Lambda payload
// ({ statusCode, body }) does not match the declared TribeSummaryResponse
// type, so it can only ever resolve to nulls. Tribe metrics are therefore
// read through the `generateTribeSummary` mutation, whose Lambda already
// serves the DynamoDB cache when one is warm. Do not reintroduce query
// documents for those fields without a matching schema + resolver change
// in resolve-guru-backend.
// =============================================================================

// =============================================================================
// DevOps Agent Queries
// =============================================================================

export const checkDevopsStatus = /* GraphQL */ `
  query CheckStatus($sessionId: String!) {
    checkDevopsStatus(sessionId: $sessionId) {
      sessionId
      status
      message
      report_url
      s3_path
      agent_session
      incident_number
      incident_datetime
    }
  }
`;

// Returns the session timeline entries for a given session, optionally
// filtered by entryType (e.g. "PLAN", "APPROVAL_RESPONSE").
// sortKey is a microsecond timestamp STRING — do NOT parse as number.
// data is AWSJSON — callers must JSON.parse() it after receiving.
export const getSessionTimeline = /* GraphQL */ `
  query GetSessionTimeline(
    $sessionId: ID!
    $entryType: String
    $afterSortKey: String
    $limit: Int
    $nextToken: String
  ) {
    getSessionTimeline(
      sessionId: $sessionId
      entryType: $entryType
      afterSortKey: $afterSortKey
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        sessionId
        sortKey
        entryType
        data
        agentId
        status
        createdAt
        ttl
      }
      nextToken
    }
  }
`;

// =============================================================================
// Runbook Queries (Phase 2: AppSync Migration)
// =============================================================================

export const getRunbook = /* GraphQL */ `
  query GetRunbook($keyword: String!) {
    getRunbook(keyword: $keyword) {
      keyword
      tribe
      is_automation
      modified_by
      modified_at
      runbook_steps {
        step_number
        description
        image_s3_key
      }
    }
  }
`;

export const listRunbooks = /* GraphQL */ `
  query ListRunbooks($tribe: String, $limit: Int, $nextToken: String) {
    listRunbooks(tribe: $tribe, limit: $limit, nextToken: $nextToken) {
      items {
        keyword
        tribe
        is_automation
        modified_by
        modified_at
        runbook_steps {
          step_number
          description
          image_s3_key
        }
      }
      nextToken
    }
  }
`;

// =============================================================================
// Orchestration Agent Feedback Queries
// =============================================================================

export const getAgentFeedback = /* GraphQL */ `
  query GetAgentFeedback($session_id: ID!, $agent_id: String!) {
    getAgentFeedback(session_id: $session_id, agent_id: $agent_id) {
      session_id
      agent_id
      feedback
      written_feedback
      status
      error
      agent_name
      user_id
      created_at
      updated_at
    }
  }
`;

export const getEvalSessionDetail = /* GraphQL */ `
  query GetEvalSessionDetail($input: GetEvalSessionDetailInput!) {
    getEvalSessionDetail(input: $input) {
      session_id
      agent_id
      eval_id
      rationale
      request_context
      response_context
      routed_to
      total_cost
      currency
      records {
        eval_id
        rationale
        request_context
        response_context
        routed_skill
        sentiment_of_feedback
        written_feedback
        created_at
      }
    }
  }
`;
